import React, { useEffect, useState } from "react";
import { Badge, Box, Flex, Spinner } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";

import { getMatchCandidates, manualMatchWorkOrder, rematchWorkOrder } from "api/ServicePlans";

/**
 * §3.9 #16 UI half — WO Review queue Match dropdown, as a modal.
 * Consumes GET /:id/match-candidates verbatim (see
 * docs/maintenance-module/design/WO_MATCHING_SERVICE.md §9).
 *
 * Match-or-Create (open decision #3): when there are no usable
 * candidates, this offers a link out to Locations rather than faking a
 * partial "create site inline" flow — there's no backend endpoint yet
 * for creating a Location + LCP from this screen in one step. Staff
 * creates the site via the existing Locations flow, then hits
 * "Retry match" here.
 */
export default function MatchCandidatesModal({
  workOrder,
  onClose,
  onMatched,
}: {
  workOrder: any;
  onClose: () => void;
  onMatched: () => void;
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<any>(null);
  const [matchingLcpId, setMatchingLcpId] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await getMatchCandidates({ data: { id: workOrder.id } });
    setResult(res);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [workOrder?.id]);

  const pickCandidate = async (lcpId: string) => {
    setMatchingLcpId(lcpId);
    const res = await manualMatchWorkOrder({ data: { id: workOrder.id, lcpId } });
    setMatchingLcpId(null);
    if (res?.id) {
      toast.add("Work order matched.", "var(--theme-ui-colors-success)");
      onMatched();
    } else {
      toast.add(res?.message || "Error matching work order.", "var(--theme-ui-colors-danger)");
    }
  };

  const retry = async () => {
    setRetrying(true);
    const res = await rematchWorkOrder({ data: { id: workOrder.id } });
    setRetrying(false);
    if (res?.status === "auto_matched") {
      toast.add("Auto-matched.", "var(--theme-ui-colors-success)");
      onMatched();
    } else {
      setResult(res);
    }
  };

  const bucketVariant = (bucket: string) => {
    if (bucket === "exact" || bucket === "high") return "success";
    if (bucket === "medium") return "warning";
    return undefined;
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>Match Location — {workOrder?.externalId || "Work Order"}</ModalHeader>
      <ModalBody>
        {loading ? (
          <Box sx={{ textAlign: "center", paddingY: "30px" }}>
            <Spinner />
          </Box>
        ) : (
          <>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} marginTop={0} marginBottom={"16px"}>
              {result?.candidates?.length
                ? `${result.candidates.length} candidate Location${result.candidates.length === 1 ? "" : "s"} found, ranked by address match.`
                : reasonToMessage(result?.reason)}
            </Subtext>

            {(result?.candidates || []).map((c: any, idx: number) => (
              <Flex
                key={c.lcpId}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "12px 14px",
                  border: "1px solid",
                  borderColor: idx === 0 ? mnt.amberStroke : "inputOutline",
                  background: idx === 0 ? mnt.amberPill : mnt.cardBg,
                  borderRadius: "8px",
                  marginBottom: "10px",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "3px", flexWrap: "wrap" }}>
                    <Span fontFamily={"Poppins"} fontWeight={600} fontSize={"13px"}>
                      {c.address?.line1 || "(no address)"}
                    </Span>
                    <Badge as="span" variant={bucketVariant(c.bucket)}>
                      {Math.round(c.score * 100)}%
                    </Badge>
                    {idx === 0 && result.candidates.length > 1 && (
                      <Span fontFamily={"Poppins"} fontSize={"11px"} fontWeight={600} color={mnt.amber}>
                        Best match
                      </Span>
                    )}
                  </Flex>
                  <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
                    {[c.address?.city, c.address?.state].filter(Boolean).join(", ")}
                    {c.clientName ? ` · ${c.clientName}` : ""}
                    {c.servicePlanName ? ` · plan: ${c.servicePlanName}` : ""}
                  </Subtext>
                </Box>
                <BasicButton
                  type="button"
                  scheme="primary"
                  onClick={() => pickCandidate(c.lcpId)}
                  $submitting={matchingLcpId === c.lcpId}
                  styles={{ flexShrink: "0" }}
                >
                  Use this Location
                </BasicButton>
              </Flex>
            ))}

            {!result?.candidates?.length && (
              <Box sx={{ textAlign: "center", padding: "20px 0" }}>
                <P fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"14px"}>
                  Not listed? Create the Location under Locations, then retry the match here.
                </P>
                <Flex sx={{ gap: "10px", justifyContent: "center" }}>
                  <BasicButton type="button" outline scheme="secondary" onClick={retry} $submitting={retrying}>
                    Retry match
                  </BasicButton>
                  <BasicButton
                    type="button"
                    outline
                    scheme="secondary"
                    onClick={() => window.open("/locations", "_blank")}
                  >
                    Open Locations
                  </BasicButton>
                </Flex>
              </Box>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton type="button" outline scheme="secondary" onClick={onClose}>
          Close
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
}

function reasonToMessage(reason?: string): string {
  switch (reason) {
    case "no_address":
      return "No Location address was extracted from this work order — match it manually.";
    case "no_candidates":
    case "no_candidates_above_threshold":
      return "No candidate Locations found near this address.";
    case "too_many_candidates":
      return "Too many possible Locations to rank automatically — narrow this down manually.";
    case "error":
      return "Something went wrong while matching. Try again or match manually.";
    default:
      return "No candidates found.";
  }
}
